/**
 * @file SecurityRoleDropdown.js
 * @description A multi-select dropdown component for selecting security role.
 */

import React, { useState, useEffect } from 'react';
import { fetchSecurityRoles } from '../../../services/securityRoleService';
import { Dropdown } from '../../helpers/DropdownSearchableSingleSelect';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import Tooltip from '../../ui/tooltips/Tooltip';
import { getText } from '../../../i18n';

/**
 * Represents a multi-select dropdown component for selecting security role.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline with the dropdown.
 * @param {Array} [props.selectedRole] - The array of selected security role IDs.
 * @param {function} props.onRoleChange - The callback function to be called when the selected role changes.
 * @param {string} [props.label="Security Role"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.isMultiTenant=false] - Determines whether to fetch multi-tenant roles.
 * @param {boolean} [props.showSelectedItemsInField=true] - Determines whether to show selected items in the field.
 * @param {boolean} [props.showSelectionBox=true] - Determines whether to show the selection box.
 * @param {boolean} [props.showSelectedCount=false] - Determines whether to show the count of selected items.
 * @returns {JSX.Element} The rendered SecurityRoleDropdown component.
 */
const SecurityRoleDropdown = ({
    className,
    inlineLabel = true,
    selectedRoleId = null,
    onRoleChange,
    label = 'Security Role',
    isRequired = false,
    isMultiTenant = false,
    showSelectedItemsInField = true,
    showSelectionBox = true,
    showSelectedCount = false,
    disabled = false,
    showToolTip = false,
    tooltipText = '',
}) => {
    const [roles, setRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getRoles = async () => {
            setIsLoading(true);
            try {
                const data = await fetchSecurityRoles(isMultiTenant);
                setRoles(data.filter(role => role.isActive));
            } catch (error) {
                console.error('Could not fetch security roles:', error);
                Toast.error(getText('msg_11c69ba3dc09'));
            } finally {
                setIsLoading(false);
            }
        };

        getRoles();
    }, [isMultiTenant]);

    const options = [
        { label: getText('msg_8b3e0e904aaf'), value: 0 },
        ...roles.map(r => ({ label: r.name, value: r.id })),
    ];

    const selectedRole = roles.find(
        r => String(r.id) === String(selectedRoleId),
    );

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="security-role-dropdown">
                {label}
                {isRequired && <span className="text-danger">*</span>}
                {showToolTip && (
                    <Tooltip html={tooltipText} placement="right">
                        <i
                            className="bi bi-info-circle"
                            style={{
                                paddingLeft: '5px',
                                fontSize: '13px',
                                color: 'var(--iNGAGED-Primary-Color)',
                            }}
                        ></i>
                    </Tooltip>
                )}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    allowSearch={true}
                    defaultValue={
                        selectedRole
                            ? selectedRole.name
                            : 'Select Security Role'
                    }
                    options={options}
                    onChange={value => {
                        onRoleChange(value);
                    }}
                    disabled={roles.length === 0 || isLoading || disabled}
                    isTitleEnabled={false}
                />
            </LoadingWrapper>
        </div>
    );
};

export default SecurityRoleDropdown;
